import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import {
  SubmitHandler,
  useForm,
  UseFormReturn,
  useWatch,
} from "react-hook-form";

import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioFieldRow from "@/components/shared/forms/RadioFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import Popup from "@/components/shared/Popup";
import {
  type CLRM,
  CLRM_TEMPLATE,
  mutationFunctions,
  Parse_CLRM_Key,
} from "@/models/clrm.tsx";
import { useCoreData, useLeaData } from "@/queries.ts";
import { dateToISOString } from "@/utils/util";
import { Container } from "react-bootstrap";
import { FaUsers } from "react-icons/fa";
import { FiEdit, FiPlus } from "react-icons/fi";
import {
  useCLRMFormLogic,
  getMostRecentClassroom,
  buildClassroomAddDefaults,
} from "./hooks/useCLRMFormLogic";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

type Option = {
  Text: string;
  Value: string;
};

export default function CLRMAddEditPopup({
  show,
  onClose,
  popupClassroom,
}: {
  show: boolean;
  onClose: () => void;
  popupClassroom?: CLRM | undefined;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const addEditForm = useForm<CLRM>({
    defaultValues: { EffectiveStartDate: dateToISOString(new Date()) },
    mode: "onSubmit",
  });

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "CLRM",
      toastMessage: "Classroom record submitted. Waiting for validation...",
      onSuccess: onClose,
    });

  const mutateFns = mutationFunctions(leaData, setSubmitResult);

  const onSubmit: SubmitHandler<CLRM> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const formId = "frmCLRM";

  return (
    <Popup
      show={show}
      header={
        popupClassroom == null ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{ fontSize: "1.25rem" }}
          >
            <FiPlus size={20} />
            <span>Add Classroom Record</span>
          </div>
        ) : (
          <div
            className="d-flex align-items-center gap-2"
            style={{ fontSize: "1.25rem" }}
          >
            <FiEdit size={20} />
            <span>Edit Classroom Record</span>
          </div>
        )
      }
      hasButton={false}
      submitFormId={formId}
      submitButtonText={
        popupClassroom ? "Update Classroom Record" : "Save Classroom Record"
      }
      submitButtonLoading={isSubmitting}
      submitButtonLoadingText="Submitting..."
      secondButtonText="Cancel"
      size="lg"
      onClose={onClose}
      onSecondButtonClick={onClose}
    >
      <Form id={formId} onSubmit={addEditForm.handleSubmit(onSubmit)}>
        <CLRMAddEditForm
          selectedClassroom={popupClassroom}
          form={addEditForm}
        />
      </Form>
    </Popup>
  );
}

export function CLRMAddEditForm({
  selectedClassroom,
  form,
}: {
  selectedClassroom?: CLRM | undefined;
  form: UseFormReturn<CLRM, any, CLRM>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/classroom/$entityid",
  });
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath)?.data;

  const classroomRef =
    params?.entityid === undefined ? {} : Parse_CLRM_Key(params.entityid);

  let newClassroomTemplate: CLRM = {
    ...CLRM_TEMPLATE,
    ...classroomRef,
  };

  if (params?.entityid && !selectedClassroom && leaData) {
    const history = leaData.entities.classrooms[params.entityid]?.records;
    const mostRecent = getMostRecentClassroom(history ?? []);
    if (mostRecent) {
      newClassroomTemplate = {
        ...newClassroomTemplate,
        ...buildClassroomAddDefaults(mostRecent),
        ...classroomRef,
      };
    }
  }

  const classroom = selectedClassroom ?? newClassroomTemplate;

  const resetKey =
    (selectedClassroom as any)?.RecordId ??
    `${(classroom as any).PreschoolCode ?? ""}|${(classroom as any).ProgramYear ?? ""}|${
      (classroom as any).LocalClassroomId ?? ""
    }`;

  const viewOnly = !leaContext.hasRole("CLRMEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("CLRM");

  const {
    register,
    control,
    reset,
    setValue,
    formState: { errors },
  } = form;

  const programYear = useWatch({
    control,
    name: "ProgramYear",
    defaultValue: classroom.ProgramYear,
  });

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => {
      return {
        Text: x.PreschoolName,
        Value: x.PreschoolCDSCode,
      };
    });

  const careTypeOptions = useMemo(() => {
    return (coreData.programTypeList ?? []) as Option[];
  }, [coreData.programTypeList]);

  const durationOptions = useMemo(() => {
    return (coreData.programDurationList ?? []) as Option[];
  }, [coreData.programDurationList]);

  const languageOptions = useMemo(() => {
    return (coreData.languagesList ?? []) as Option[];
  }, [coreData.languagesList]);

  const contractTypeOptions = useMemo(() => {
    return (coreData.contractTypesOfferedList ?? []) as Option[];
  }, [coreData.contractTypesOfferedList]);

  const clrmLogic = useCLRMFormLogic({
    form,
    classroom,
    resetKey,
    careTypeOptions,
    durationOptions,
    languageOptions,
    contractTypeOptions,
  });

  useMultiSelectChipListLabel(
    "ProgramTypeOffered1",
    "Program Type (required)",
    clrmLogic.careTypes.value
  );
  useMultiSelectChipListLabel(
    "ContractTypeOffered1",
    "Contract Types",
    clrmLogic.contractTypes.value
  );
  useMultiSelectChipListLabel(
    "ProgramDurationOffered1",
    "Program Durations (required)",
    clrmLogic.programDurations.value
  );

  // See MultiSelectToggleButton: gives keyboard users a way to browse each
  // MultiSelect's full option list without having to type first.
  const [careTypesOpened, setCareTypesOpened] = useState(false);
  const [contractTypesOpened, setContractTypesOpened] = useState(false);
  const [programDurationsOpened, setProgramDurationsOpened] = useState(false);

  useMultiSelectListboxMultiselectable("ProgramTypeOffered1", careTypesOpened);
  useMultiSelectListboxMultiselectable(
    "ContractTypeOffered1",
    contractTypesOpened
  );
  useMultiSelectListboxMultiselectable(
    "ProgramDurationOffered1",
    programDurationsOpened
  );

  useEffect(() => {
    register("ContractTypeOffered1" as any);
    register("ContractTypeOffered2" as any);
  }, [register]);

  useEffect(() => {
    reset(classroom);
  }, [selectedClassroom]);

  useEffect(() => {
    if (!selectedClassroom) {
      reset(classroom);
    }
  }, []);

  const handleLanguagesChange = (items: Option[]) => {
    clrmLogic.languages.onChange(items);
    form.trigger("LanguageUsedInClassroom1");
  };

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaUsers className="section-icon" />
          <h5 className="section-title">Classroom Information</h5>
        </div>
        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Program Year is required" })}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) =>
                !!classroom.PreschoolCode && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(id, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="LocalClassroomId"
              labelText="Classroom ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!classroom.LocalClassroomId}
                  maxLength={50}
                  {...register(id, { required: "Classroom ID is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    required: "Effective start date is required",
                    validate: clrmLogic.validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="ClassroomName"
              labelText="Classroom Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={355}
                  {...register(id, { required: "Classroom name is required" })}
                  onBlur={(e) => {
                    const trimmed = e.target.value.replace(/\s+/g, " ").trim();
                    setValue(id, trimmed);
                  }}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaUsers className="section-icon" />
          <h5 className="section-title">Classroom Details</h5>
        </div>
        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="VolunteerFrequency"
              labelText="Volunteer Frequency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    required: "Volunteer frequency is required",
                  })}
                  options={coreData.volunteerFrequencyList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramTypeOffered1"
              labelText="Program Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  <input
                    type="hidden"
                    {...register("ProgramTypeOffered1", {
                      validate: () => {
                        const val1 = form.getValues("ProgramTypeOffered1");
                        const val2 = form.getValues("ProgramTypeOffered2");
                        if (!val1 && !val2) {
                          return "Program Type is required";
                        }
                        return true;
                      },
                    })}
                  />
                  <input type="hidden" {...register("ProgramTypeOffered2")} />
                  <MultiSelect
                    id={id}
                    ariaLabel="Program Type"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={viewOnly}
                    required
                    data={careTypeOptions}
                    textField="Text"
                    dataItemKey="Value"
                    value={clrmLogic.careTypes.value}
                    onChange={(e: MultiSelectChangeEvent) => {
                      clrmLogic.careTypes.onChange(e.value as Option[]);
                      form.trigger("ProgramTypeOffered1");
                    }}
                    placeholder="Select up to 2 program types"
                    className={viewOnly ? "kendo-ms-disabled" : undefined}
                    valid={!errors.ProgramTypeOffered1}
                    validationMessage={
                      errors.ProgramTypeOffered1?.message as string | undefined
                    }
                    inputAttributes={
                      errors.ProgramTypeOffered1
                        ? { "aria-invalid": true }
                        : undefined
                    }
                    opened={careTypesOpened}
                    onOpen={() => setCareTypesOpened(true)}
                    onClose={() => setCareTypesOpened(false)}
                    itemRender={multiSelectSelectedItemRender}
                    popupSettings={{ className: `${id}-listbox-popup` }}
                    tagRender={multiSelectTagRender}
                    suffix={
                      <>
                        <MultiSelectToggleButton
                          opened={careTypesOpened}
                          onToggle={() => setCareTypesOpened((prev) => !prev)}
                          disabled={viewOnly}
                        />
                        {clrmLogic.careTypes.value.length > 0 && (
                          <MultiSelectClearButton
                            onClear={() => {
                              clrmLogic.careTypes.onChange([]);
                              form.trigger("ProgramTypeOffered1");
                            }}
                            disabled={viewOnly}
                          />
                        )}
                      </>
                    }
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ContractTypeOffered1"
              labelText="Contract Types"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <MultiSelect
                  id={id}
                  disabled={viewOnly}
                  data={contractTypeOptions}
                  textField="Text"
                  dataItemKey="Value"
                  value={clrmLogic.contractTypes.value}
                  onChange={(e: MultiSelectChangeEvent) =>
                    clrmLogic.contractTypes.onChange(e.value as Option[])
                  }
                  placeholder="Select up to 2 contract types"
                  className={viewOnly ? "kendo-ms-disabled" : undefined}
                  opened={contractTypesOpened}
                  onOpen={() => setContractTypesOpened(true)}
                  onClose={() => setContractTypesOpened(false)}
                  itemRender={multiSelectSelectedItemRender}
                  popupSettings={{ className: `${id}-listbox-popup` }}
                  tagRender={multiSelectTagRender}
                  suffix={
                    <>
                      <MultiSelectToggleButton
                        opened={contractTypesOpened}
                        onToggle={() =>
                          setContractTypesOpened((prev) => !prev)
                        }
                        disabled={viewOnly}
                      />
                      {clrmLogic.contractTypes.value.length > 0 && (
                        <MultiSelectClearButton
                          onClear={() => clrmLogic.contractTypes.onChange([])}
                          disabled={viewOnly}
                        />
                      )}
                    </>
                  }
                />
              )}
            </FormFieldRow>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="SettingTypeOffered"
              labelText="Setting Types"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Settings type is required" })}
                  options={coreData.settingTypesOfferedList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramDurationOffered1"
              labelText="Program Durations"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  <input
                    type="hidden"
                    {...register("ProgramDurationOffered1", {
                      validate: () => {
                        const val1 = form.getValues("ProgramDurationOffered1");
                        const val2 = form.getValues("ProgramDurationOffered2");
                        if (!val1 && !val2) {
                          return "Program Duration is required";
                        }
                        return true;
                      },
                    })}
                  />
                  <input
                    type="hidden"
                    {...register("ProgramDurationOffered2")}
                  />
                  <MultiSelect
                    id={id}
                    ariaLabel="Program Durations"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={viewOnly}
                    required
                    data={durationOptions}
                    textField="Text"
                    dataItemKey="Value"
                    value={clrmLogic.programDurations.value}
                    onChange={(e: MultiSelectChangeEvent) => {
                      clrmLogic.programDurations.onChange(e.value as Option[]);
                      form.trigger("ProgramDurationOffered1");
                    }}
                    placeholder="Select up to 2 program durations"
                    className={viewOnly ? "kendo-ms-disabled" : undefined}
                    valid={!errors.ProgramDurationOffered1}
                    validationMessage={
                      errors.ProgramDurationOffered1?.message as
                        | string
                        | undefined
                    }
                    inputAttributes={
                      errors.ProgramDurationOffered1
                        ? { "aria-invalid": true }
                        : undefined
                    }
                    opened={programDurationsOpened}
                    onOpen={() => setProgramDurationsOpened(true)}
                    onClose={() => setProgramDurationsOpened(false)}
                    itemRender={multiSelectSelectedItemRender}
                    popupSettings={{ className: `${id}-listbox-popup` }}
                    tagRender={multiSelectTagRender}
                    suffix={
                      <>
                        <MultiSelectToggleButton
                          opened={programDurationsOpened}
                          onToggle={() =>
                            setProgramDurationsOpened((prev) => !prev)
                          }
                          disabled={viewOnly}
                        />
                        {clrmLogic.programDurations.value.length > 0 && (
                          <MultiSelectClearButton
                            onClear={() => {
                              clrmLogic.programDurations.onChange([]);
                              form.trigger("ProgramDurationOffered1");
                            }}
                            disabled={viewOnly}
                          />
                        )}
                      </>
                    }
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="LanguageProgramType"
              labelText="Language Program Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Program type is required" })}
                  options={coreData.languageProgramType}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <RadioFieldRow
              name="MHCSOffered"
              labelText="MHCS Offered"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "MHCS is required" })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="LanguageUsedInClassroom1"
              labelText="Language(s) Used in Classroom"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  <input
                    type="hidden"
                    {...register("LanguageUsedInClassroom1", {
                      validate: () => {
                        const val1 = form.getValues("LanguageUsedInClassroom1");
                        const val2 = form.getValues("LanguageUsedInClassroom2");
                        const val3 = form.getValues("LanguageUsedInClassroom3");
                        const val4 = form.getValues("LanguageUsedInClassroom4");
                        const val5 = form.getValues("LanguageUsedInClassroom5");
                        if (!val1 && !val2 && !val3 && !val4 && !val5) {
                          return "Language(s) Used in Classroom is required";
                        }
                        return true;
                      },
                    })}
                  />
                  <input
                    type="hidden"
                    {...register("LanguageUsedInClassroom2")}
                  />
                  <input
                    type="hidden"
                    {...register("LanguageUsedInClassroom3")}
                  />
                  <input
                    type="hidden"
                    {...register("LanguageUsedInClassroom4")}
                  />
                  <input
                    type="hidden"
                    {...register("LanguageUsedInClassroom5")}
                  />
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Used in Classroom"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={viewOnly}
                    required
                    invalid={!!validationProps["aria-invalid"]}
                    data={languageOptions}
                    value={clrmLogic.languages.value}
                    onChange={handleLanguagesChange}
                    placeholder="Select up to 5 languages"
                    className={viewOnly ? "kendo-ms-disabled" : undefined}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
      <span
        id="spnAddOrEditClassroomErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
